import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  alpha,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"

import {
  CloseRounded,
  LegendToggleRounded
} from "@mui/icons-material"

import chroma from "chroma-js"

import { describeResponseBody } from "../../../lib/utils"

import { catchMessage } from "../../../store/reducers/errors"

import "./legend.css"

function Legend(props) {
  const { mapConfig, selectedLanguage, setMapConfig, view } = props

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const theme = useTheme()

  const accountinfo = useSelector(state => state.accountinfo)

  const [legendVis, setLegendVis] = useState(true)

  useEffect(() => {
    mapConfig.layers.forEach(layer => {
      if (!layer.wms || layer.legendFetched || layer.inactive || layer.disabled) return

      let url
      if (layer.legendUrl) {
        url = layer.legendUrl
      } else {
        url = new URL(layer.url)
        url.search = new URLSearchParams({
          request: "GetLegendGraphic",
          layer: layer.layer,
          style: layer.styles || "",
          format: "application/json",
          ...(selectedLanguage && { language: selectedLanguage })
        })
        url = url.toString()
      }

      fetch(url, {
        headers: {
          ...(accountinfo?.token && { Authorization: `Bearer ${accountinfo.token}` })
        }
      })
        .then(async res => {
          // Read as text first: an error answer is XML or a wrapped message, and res.json()
          // would turn that into an unreadable parse error
          const text = await res.text()

          if (!res.ok) throw new Error(describeResponseBody(text) || `Legend fetch failed: ${res.status}`)

          try {
            return JSON.parse(text)
          } catch (error) {
            throw new Error(describeResponseBody(text) || error.message)
          }
        })
        .then(json => {
          let legend = []
          if (json?.Legend) {
            legend = json.Legend[0]
          }
          if (json?.layers) {
            const splittedLayers = layer.layer.replace("show:", "").split(",")
            legend = json.layers.filter(ly => splittedLayers.includes(String(ly.layerId)))
          }
          setMapConfig(prev => ({
            ...prev,
            layers: prev.layers.map(l =>
              l.key === layer.key
                ? { ...l, legend, legendFetched: true }
                : l
            )
          }))
        })
        .catch(error => {
          dispatch(catchMessage({ exception: error }, "Fetch legend"))
        })
    })
  }, [mapConfig.layers.length, selectedLanguage, accountinfo?.token])

  const renderGeoJsonIcon = (style) => {
    const isPolyLine = !style.fillColor
    const isRadius = Boolean(style.radius)

    let fill = style.fillColor
    let fillOpacity = 1
    if (style.hasOwnProperty("fillOpacity") && style.fillColor !== "transparent") {
      fillOpacity = style.fillOpacity
      fill = alpha(style.fillColor, style.fillOpacity)
    }

    let color = style.color
    let opacity = 1
    if (style.hasOwnProperty("opacity")) {
      opacity = style.opacity
      color = alpha(style.color, style.opacity)
    }

    switch (style.pointerType) {
      case "hexagon":
        return <svg
          width="100%" height="100%" viewBox="0 0 10 10"
          style={{
            fillRule: "evenodd", clipRule: "evenodd", strokeLinejoin: "round", strokeMiterlimit: 2,
            width: 20, height: 20,
            margin: theme.spacing(0.5, 1, 0, 0)
          }}
        >
          <path
            id="marker"
            d="M5,0l4.33,2.5l0,5l-4.33,2.5l-4.33,-2.5l-0,-5l4.33,-2.5Z"
            style={{ opacity: fillOpacity, fill: fill }}
          />
          <path
            id="outline"
            style={{ opacity: opacity, fill: color }}
            d="M5,0l4.33,2.5l0,5l-4.33,2.5l-4.33,-2.5l-0,-5l4.33,-2.5Zm-0,0.577l3.83,2.212c0,-0 0,4.422 0,4.422c0,0 -3.83,2.212 -3.83,2.212c0,-0 -3.83,-2.212 -3.83,-2.212c-0,0 -0,-4.422 -0,-4.422c-0,-0 3.83,-2.212 3.83,-2.212Z"
          />
        </svg>

      case "diamond":
        return <svg
          width="100%" height="100%" viewBox="0 0 10 10"
          style={{
            fillRule: "evenodd", clipRule: "evenodd", strokeLinejoin: "round", strokeMiterlimit: 2,
            width: 20, height: 20,
            margin: theme.spacing(0.5, 1, 0, 0)
          }}
        >
          <path
            id="marker"
            d="M9.95,5l-4.95,-4.95l-4.95,4.95l4.95,4.95l4.95,-4.95Z"
            style={{ opacity: fillOpacity, fill: fill }}
          />
          <path
            id="outline"
            d="M9.95,5l-4.95,-4.95l-4.95,4.95l4.95,4.95l4.95,-4.95Zm-0.707,-0l-4.243,4.243c0,-0 -4.243,-4.243 -4.243,-4.243c0,0 4.243,-4.243 4.243,-4.243l4.243,4.243Z"
            style={{ opacity: opacity, fill: color }}
          />
        </svg>

      case "hospital":
        return <svg
          width="100%" height="100%" viewBox="0 0 10 10"
          style={{
            fillRule: "evenodd", clipRule: "evenodd", strokeLinejoin: "round", strokeMiterlimit: 2,
            width: 20, height: 20,
            margin: theme.spacing(0.5, 1, 0, 0)
          }}
        >
          <circle id="background" cx="5" cy="5" r="5" style={{ fill: "#ffffff" }} />
          <path
            id="outline"
            d="M5,0c2.76,0 5,2.24 5,5c0,2.76 -2.24,5 -5,5c-2.76,0 -5,-2.24 -5,-5c0,-2.76 2.24,-5 5,-5Zm0,0.5c2.484,0 4.5,2.016 4.5,4.5c0,2.484 -2.016,4.5 -4.5,4.5c-2.484,0 -4.5,-2.016 -4.5,-4.5c0,-2.484 2.016,-4.5 4.5,-4.5Z"
            style={{ opacity: opacity, fill: color }}
          />
          <path id="marker"
            d="M5.8,4.2l0,-2.2l-1.6,-0l0,2.2l-2.2,0l0,1.6l2.2,0l0,2.2l1.6,0l0,-2.2l2.2,0l0,-1.6l-2.2,0Z"
            style={{ opacity: fillOpacity, fill: fill }}
          />
        </svg>

      case "spoor":
        return <div style={{ display: "flex", height: 18, position: "relative" }}>
          <div style={{
            position: "absolute",
            width: 20,
            height: 3,
            borderRadius: theme.shape.borderRadius - 2,
            background: fill || "transparent",
            left: 0,
            top: 8
          }} />
          <svg style={{ position: "absolute", left: 2, top: 6, width: 20, height: 14 }}>
            <line x1="0" y1="0" x2="20" y2="0" style={{ stroke: "#212121", strokeWidth: 14, strokeDasharray: [2, 5] }} />
          </svg>
        </div>

      case "marker":
        return <svg
          width="100%" height="100%" viewBox="0 0 10 10"
          style={{
            fillRule: "evenodd",
            clipRule: "evenodd",
            strokeLinejoin: "round",
            strokeMiterlimit: 2,
            width: 22, height: 22,
            margin: theme.spacing(0.5, 1, 0, 0)
          }}
        >
          <path
            id="outline"
            d="M8.558,4.183l0.042,-0.566l-0,-0.178c-0.005,-0.169 -0.026,-0.335 -0.057,-0.504c-0.087,-0.465 -0.259,-0.899 -0.518,-1.296c-0.198,-0.303 -0.435,-0.571 -0.714,-0.804c-0.195,-0.161 -0.403,-0.299 -0.624,-0.417l-0.001,-0c-0.219,-0.116 -0.447,-0.209 -0.686,-0.276c-0.295,-0.087 -0.6,-0.13 -0.908,-0.142l-0.537,0.029c-0.209,0.024 -0.414,0.067 -0.619,0.131c-0.364,0.113 -0.702,0.277 -1.015,0.499c-0.266,0.189 -0.501,0.407 -0.709,0.659c-0.215,0.263 -0.389,0.548 -0.524,0.86c-0.161,0.373 -0.257,0.765 -0.283,1.168l-0.005,0.438c0.011,0.118 0.016,0.233 0.031,0.347c0.024,0.204 0.065,0.41 0.113,0.611c0.06,0.246 0.136,0.486 0.22,0.724c0,0.001 0.001,0.001 0.001,0.001c0.128,0.348 0.275,0.685 0.442,1.019c0.106,0.219 0.223,0.433 0.343,0.644c-0,0 0.001,0 0.001,0.001c0.213,0.37 0.438,0.731 0.677,1.085c0.204,0.303 0.356,0.505 0.531,0.73l0.577,0.744c0.103,0.13 0.311,0.31 0.661,0.31c0.349,-0 0.584,-0.217 0.662,-0.32c0.021,-0.027 0.38,-0.468 0.544,-0.687c0.171,-0.225 0.348,-0.458 0.544,-0.747c0.221,-0.326 0.429,-0.654 0.629,-0.993l0,-0.001c0.205,-0.356 0.397,-0.72 0.57,-1.095c0.145,-0.315 0.271,-0.639 0.38,-0.971l0,-0.002c0.103,-0.332 0.185,-0.661 0.232,-1.001Zm-0.77,-0.731c-0.002,0.053 -0.002,0.107 -0,0.171c-0.002,0.143 -0.015,0.296 -0.035,0.453c-0.04,0.291 -0.11,0.577 -0.201,0.862c-0.097,0.298 -0.213,0.593 -0.345,0.88c-0.16,0.353 -0.34,0.693 -0.533,1.025c-0.189,0.323 -0.388,0.636 -0.599,0.947c-0,0 -0.001,0 -0.001,0.001c-0.186,0.278 -0.382,0.548 -0.582,0.812c-0.161,0.21 -0.324,0.419 -0.493,0.624c-0.119,-0.145 -0.237,-0.292 -0.353,-0.441c-0.258,-0.336 -0.507,-0.674 -0.744,-1.025c-0.227,-0.337 -0.444,-0.682 -0.646,-1.033l-0.321,-0.601c-0.155,-0.309 -0.29,-0.618 -0.406,-0.936c-0.075,-0.209 -0.143,-0.424 -0.194,-0.638c-0.041,-0.168 -0.077,-0.345 -0.097,-0.52l-0.027,-0.306c-0.007,-0.107 -0.005,-0.216 0.003,-0.325c0.022,-0.312 0.094,-0.613 0.22,-0.9c0.104,-0.242 0.239,-0.465 0.406,-0.668c0.001,-0 0.001,-0.001 0.001,-0.001c0.16,-0.196 0.343,-0.364 0.548,-0.511c0.242,-0.17 0.504,-0.299 0.786,-0.386l0.48,-0.102c0.137,-0.016 0.274,-0.027 0.411,-0.022c0.238,0.008 0.476,0.041 0.709,0.109l0.002,0c0.184,0.053 0.36,0.124 0.529,0.213c0.172,0.092 0.335,0.201 0.486,0.325c0.213,0.18 0.4,0.388 0.552,0.623l-0,0.001c0.2,0.307 0.333,0.643 0.401,1.002l0.043,0.367Zm-2.791,2.153c1.102,-0 1.999,-0.892 2.005,-1.994c0.006,-1.11 -0.897,-2.007 -1.991,-2.015c-1.116,-0.007 -2.015,0.899 -2.018,2c-0,1.109 0.903,2.009 2.004,2.009Zm0,-0.812c-0.655,-0 -1.192,-0.536 -1.192,-1.196c0.003,-0.654 0.538,-1.192 1.201,-1.189c0.651,0.005 1.188,0.537 1.184,1.198l-0,0.001c-0.004,0.655 -0.539,1.186 -1.193,1.186Z"
            style={{ fill: color, opacity: opacity }}
          />
          <path
            id="marker"
            d="M8.194,3.617c-0.002,0.163 -0.016,0.335 -0.038,0.51c-0.044,0.318 -0.12,0.625 -0.216,0.933c-0.103,0.316 -0.225,0.626 -0.363,0.928c-0.167,0.363 -0.353,0.715 -0.551,1.059c-0.195,0.331 -0.399,0.653 -0.616,0.972c-0.098,0.146 -0.236,0.349 -0.376,0.55c-0.132,0.191 -0.281,0.401 -0.452,0.612c-0.419,0.517 -0.744,0.498 -1.158,-0.023c-0.186,-0.236 -0.342,-0.444 -0.46,-0.606c-0.136,-0.185 -0.27,-0.372 -0.398,-0.563c-0.233,-0.347 -0.455,-0.699 -0.663,-1.059c-0.116,-0.205 -0.229,-0.412 -0.333,-0.623c-0.16,-0.322 -0.301,-0.645 -0.423,-0.978c-0.08,-0.224 -0.152,-0.451 -0.208,-0.681c-0.044,-0.186 -0.083,-0.377 -0.105,-0.568c-0.014,-0.108 -0.02,-0.216 -0.028,-0.324c-0.008,-0.128 -0.006,-0.255 0.004,-0.383c0.024,-0.357 0.107,-0.704 0.251,-1.033c0.12,-0.278 0.274,-0.532 0.465,-0.765c0.184,-0.224 0.394,-0.417 0.629,-0.584c0.277,-0.196 0.577,-0.344 0.9,-0.443c0.181,-0.056 0.363,-0.094 0.552,-0.116c0.155,-0.02 0.314,-0.031 0.471,-0.026c0.274,0.009 0.545,0.048 0.811,0.125c0.211,0.061 0.413,0.142 0.607,0.244c0.197,0.105 0.383,0.23 0.554,0.371c0.247,0.208 0.46,0.445 0.635,0.715c0.229,0.351 0.381,0.736 0.459,1.149c0.028,0.147 0.045,0.294 0.05,0.441c-0.002,0.052 -0.002,0.102 -0,0.166Zm-3.197,1.582c0.878,-0 1.594,-0.712 1.599,-1.59c0.005,-0.887 -0.715,-1.601 -1.587,-1.607c-0.89,-0.006 -1.607,0.717 -1.61,1.595c-0,0.884 0.72,1.602 1.598,1.602Z"
            style={{ fill: fill, opacity: fillOpacity, fillRule: "nonzero" }}
          />
        </svg>

      default:
        const size = isPolyLine ? 20 : 16
        return (
          <div style={{
            width: style.weight ? size - (style.weight >= 2 ? 2 : style.weight) : size,
            minWidth: style.weight ? size - (style.weight >= 2 ? 2 : style.weight) : size,
            height: isPolyLine ? 4 : style.weight ? (size - (style.weight >= 2 ? 2 : style.weight)) : size,
            borderRadius: isRadius ? "50%" : (theme.shape.borderRadius - 2),
            background: (isPolyLine ? color : fill) || "transparent",
            border: !isPolyLine && `${style.weight >= 2 ? 2 : (style.weight || 0)}px ${style.dashArray ? "dashed" : "solid"} ${color}`,
            margin: isPolyLine ? theme.spacing(1, 1, 0.5, 0) : theme.spacing(0.5, 1, 0, 0),
            transform: isPolyLine ? "rotate(-45deg)" : null
          }} />
        )
    }
  }

  const renderGeoJSONLayersLegend = (geojsonLayers) => {
    // Alle unieke layer styles bijhouden
    const uniqueLayerStyles = []

    geojsonLayers.forEach(geojsonLayer => {
      const options = { ...geojsonLayer.options, pointerType: geojsonLayer.pointerType }//, panel: geojsonLayer.panel }

      if (uniqueLayerStyles.length > 0) {
        // De vier attributen wat een unieke style maakt
        if (uniqueLayerStyles.find(USL => JSON.stringify(USL) === JSON.stringify(options))) {
        } else {
          uniqueLayerStyles.push(options)
        }
      } else {
        uniqueLayerStyles.push(options)
      }
    })

    return uniqueLayerStyles.map((style, index) => {
      // Alle layers met deze style verzamelen
      const layersWithSameStyle = geojsonLayers.filter(l => {
        const options = { ...l.options, pointerType: l.pointerType }//, panel: l.panel }
        return JSON.stringify(options) === JSON.stringify(style)
      })

      return (
        <div
          key={index}
          style={{ display: "flex", alignItems: "center" }}
          className="legend-item"
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            {layersWithSameStyle.map((l, i) => (
              <Typography key={i} variant="body2" sx={{ lineHeight: 1, fontWeight: 500 }}>{t(l.name)}:</Typography>
            ))}
            {renderGeoJsonIcon(style)}
          </div>
        </div>
      )
    })
  }

  const renderChromaGradient = (colormap, steps = 200) => {
    const scale = chroma.scale(colormap.map(entry => entry.color)).mode("lrgb").colors(steps)

    return (
      <div style={{ display: "flex" }}>
        <div style={{ display: "flex", flexDirection: "column", marginRight: theme.spacing(0.25) }}>
          {[...Array(steps)].map((step, index) => (
            <div key={index} style={{ display: "flex", alignItems: "center", backgroundColor: scale[index], height: 1, flexGrow: 1, width: 18 }} />
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {colormap.map((entry, i) => (
            <Typography key={i} variant="body2" sx={{ lineHeight: 1.25, height: 14 }}>{entry.label}</Typography>
          ))}
        </div>
      </div>
    )
  }

  const renderGeoserverLegend = (legend) => {
    if (Array.isArray(legend)) {
      return legend.map((item, index) => {
        return <div key={index} style={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>{item.layerName}</Typography>
          {item.legend?.map((legend, ix) => (
            <div key={ix} style={{ display: "flex", alignItems: "center" }}>
              <img src={`data:image/png;base64,${legend.imageData}`} width={14} height={14} style={{ marginRight: theme.spacing(0.5) }} />
              <Typography variant="caption" sx={{ lineHeight: 1.2 }}>{legend.label}</Typography>
            </div>
          ))}
        </div>
      })
    } else {
      return legend.rules.map((rule, index) => (
        <div key={index} style={{ display: "flex", justifyContent: "start", alignItems: "center", minHeight: 18 }}>
          {rule.symbolizers && rule.symbolizers.map((symbolizer, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", position: "relative" }}>
              {Object.keys(symbolizer).map((symbolizerType, j) => {
                const fillColor = symbolizer[symbolizerType]["fill"]
                const fillOpacity = symbolizer[symbolizerType].hasOwnProperty("fill-opacity") ? Number(symbolizer[symbolizerType]["fill-opacity"]) : 1
                const strokeWidth = symbolizer[symbolizerType].hasOwnProperty("stroke-width") ? Number(symbolizer[symbolizerType]["stroke-width"]) : 0
                const strokeColor = symbolizer[symbolizerType]["stroke"]
                const strokeOpacity = symbolizer[symbolizerType].hasOwnProperty("stroke-opacity") ? Number(symbolizer[symbolizerType]["stroke-opacity"]) : 1

                return (
                  <React.Fragment key={j}>
                    {symbolizerType === "Raster" && (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {symbolizer[symbolizerType].colormap.type === "intervals" && (
                          symbolizer[symbolizerType].colormap.entries.map((entry, k) => (
                            <div key={k} style={{ display: "flex", alignItems: "center" }}>
                              <div
                                style={{
                                  width: 18,
                                  height: 18,
                                  background: entry.color,
                                  opacity: entry.hasOwnProperty("opacity") ? Number(entry.opacity) : 1
                                }}
                              />
                              {entry.hasOwnProperty("opacity") && Number(entry.opacity) === 0 && <CloseRounded sx={{ width: 16, height: 16, position: "absolute", color: theme.palette.error.main }} />}
                              <Typography variant="body2" sx={{ marginLeft: theme.spacing(0.5) }}>{entry.label}</Typography>
                            </div>
                          ))
                        )}
                        {symbolizer[symbolizerType].colormap.type === "ramp" && (
                          renderChromaGradient(symbolizer[symbolizerType].colormap.entries)
                        )}
                      </div>
                    )}
                    {symbolizerType === "Line" && (
                      <div
                        style={{
                          width: 16,
                          height: strokeWidth,
                          background: strokeColor,
                          transform: "rotate(-45deg)"
                        }}
                      />
                    )}
                    {symbolizerType === "Point" && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, position: "absolute" }}>
                        <img
                          src={symbolizer[symbolizerType].url}
                          width={symbolizer[symbolizerType].size}
                          height={symbolizer[symbolizerType].size}
                        />
                      </div>
                    )}
                    {(symbolizerType === "Polygon" && !symbolizer[symbolizerType]["graphic-fill"]) && (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div
                          style={{
                            width: 16 - (strokeWidth * 2),
                            height: 16 - (strokeWidth * 2),
                            background: alpha(fillColor, fillOpacity),
                            ...strokeColor && {
                              border: `${strokeWidth}px solid ${alpha(strokeColor, strokeOpacity)}`
                            }
                          }}
                        />
                        {rule.name ? <Typography variant="body2" sx={{ marginLeft: theme.spacing(0.5) }}>{rule.name}</Typography> : null}
                      </div>
                    )}
                    {symbolizerType === "Text" && (
                      <Typography variant="body2">{symbolizer[symbolizerType].label}</Typography>
                    )}
                  </React.Fragment>
                )
              })}
              {rule.title && <Typography variant="body2" sx={{ marginLeft: theme.spacing(0.5) }}>{rule.title}</Typography>}
              {/* {rule.name && <Typography variant="body2" sx={{ marginLeft: theme.spacing(0.5) }}>{rule.name}</Typography>} */}
            </div>
          ))}
        </div>
      ))
    }
  }

  const renderWMSLegend = (wmsLayers) => {
    const uniqueLayerStyles = []

    wmsLayers.forEach(wmsLayer => {
      const style = wmsLayer.styles

      if (style === "" || !uniqueLayerStyles.find(u => u.styles === style)) {
        uniqueLayerStyles.push({
          ...wmsLayer,
          originalStyles: style,
          layer: wmsLayer.layer.split(",")[0].trim(),
          styles: style.split(",")[0].trim(),
        })
      }
    })

    return uniqueLayerStyles.map((uniqueLayerStyle, index) => {
      const layersWithSameStyle = wmsLayers.filter(l =>
        l.styles === ""
          ? (l.key === uniqueLayerStyle.key)
          : (l.styles === uniqueLayerStyle.originalStyles)
      )

      const baseUrl = uniqueLayerStyle.legendUrl || uniqueLayerStyle.url
      let url = `${baseUrl}?request=GetLegendGraphic&layer=${uniqueLayerStyle.layer}&style=${uniqueLayerStyle.styles}&format=image/png&version=1`
      if (accountinfo?.token) {
        url += `&token=${accountinfo.token}`
      }

      return (
        <div key={index} style={{ display: "flex", flexDirection: "column" }} className="legend-item">
          {layersWithSameStyle.map((l, i) => (
            <React.Fragment key={i} >
              <Typography variant="body2" sx={{ lineHeight: 1, fontWeight: 500 }}>
                {t(l.name)}:
              </Typography>
              {l.subtitle && <Typography variant="body2" sx={{ lineHeight: 1, fontWeight: 400 }}>
                {t(l.subtitle)}
              </Typography>}
            </React.Fragment>
          ))}
          {uniqueLayerStyle.legend
            ? renderGeoserverLegend(uniqueLayerStyle.legend)
            : <img src={url} alt={t(uniqueLayerStyle.name)} style={{ width: "fit-content", maxWidth: "100%" }} />}
        </div>
      )
    })
  }

  const renderTDLegend = (tdLayers) => {
    // Layers sharing a legendGroup are drawn on the same scale, so their legend image is
    // identical: list the names together and fetch the image once instead of per layer
    const groups = []

    tdLayers.forEach(tdLayer => {
      const groupKey = tdLayer.legendGroup || tdLayer.key
      const group = groups.find(g => g.key === groupKey)

      if (group) {
        group.layers.push(tdLayer)
      } else {
        groups.push({ key: groupKey, layers: [tdLayer] })
      }
    })

    return groups.map((group, index) => {
      const [firstLayer] = group.layers
      const grouped = group.layers.length > 1

      const layerId = Array.isArray(firstLayer.layer) ? firstLayer.layer[0] : firstLayer.layer
      let url = `${firstLayer.legendUrl}?request=GetLegendGraphic&format=image/png&layers=${layerId}`
      if (accountinfo?.token) {
        url += `&token=${accountinfo.token}`
      }

      return (
        <div key={index} style={{ display: "flex", flexDirection: "column" }} className="legend-item">
          {grouped
            ? (
              <React.Fragment>
                {group.layers.map((l, i) => (
                  <Typography key={i} variant="body2" sx={{ lineHeight: 1.3, fontWeight: 500 }}>
                    {t(l.name)}{l.subtitle ? ` ${t(l.subtitle)}` : ""}
                  </Typography>
                ))}
                {/* The unit belongs to the shared scale, so it is shown once above the image */}
                {firstLayer.unit && <Typography variant="body2" sx={{ lineHeight: 1, fontWeight: 400 }}>
                  {`[${firstLayer.unit}]`}
                </Typography>}
              </React.Fragment>
            )
            : (
              <React.Fragment>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t(firstLayer.name)}{firstLayer.unit && !firstLayer.subtitle ? ` [${firstLayer.unit}]` : ""}:
                </Typography>
                {firstLayer.subtitle && <Typography variant="body2" sx={{ lineHeight: 1, fontWeight: 400 }}>
                  {t(firstLayer.subtitle)}{firstLayer.unit ? ` [${firstLayer.unit}]` : ""}
                </Typography>}
              </React.Fragment>
            )}
          <img src={url} alt={t(firstLayer.name)} style={{ width: "fit-content" }} />
        </div>
      )
    })
  }

  const renderCustomLegendLayers = (layers) => {
    return layers.map((l, i) => (
      <div
        key={i}
        style={{ display: "flex", flexDirection: "column" }}
        className="legend-item"
      >
        <Typography variant="body2" sx={{ lineHeight: 1, fontWeight: 500 }}>{t(l.name)}:</Typography>
        {l.renderLegend(l)}
      </div>
    ))
  }

  const geojsonLayers = mapConfig.layers.filter(l => l.legend !== false && !l.wms && !l.renderLegend && (!l.view || l.view && l.view.includes(view)))
  const wmsLayers = mapConfig.layers.filter(l => l.legend !== false && l.wms && (!l.view || l.view && l.view.includes(view)))
  const tdLayers = mapConfig.timeDimensionLayers?.filter(l => l.legend !== false && (!l.view || l.view && l.view.includes(view))) || []
  const customLegendLayers = mapConfig.layers.filter(l => l.legend !== false && l.renderLegend && (!l.view || l.view && l.view.includes(view)))

  let activeGeojonLayers = geojsonLayers.filter(l => l.checked)
  let activeWMSLayers = wmsLayers.filter(l => l.checked)
  let activeTDLayers = tdLayers.filter(l => l.checked)
  let activeCustomLegendLayers = customLegendLayers.filter(l => l.checked)

  if (props.layer) {
    activeGeojonLayers = geojsonLayers.filter(ly => ly.key === props.layer.key)
    if (activeGeojonLayers.length > 0) return renderGeoJSONLayersLegend(activeGeojonLayers)

    activeWMSLayers = wmsLayers.filter(ly => ly.key === props.layer.key)
    if (activeWMSLayers.length > 0) return renderWMSLegend(activeWMSLayers)

    activeTDLayers = tdLayers.filter(ly => ly.key === props.layer.key)
    if (activeTDLayers.length > 0) return renderTDLegend(activeTDLayers)

    activeCustomLegendLayers = customLegendLayers.filter(ly => ly.key === props.layer.key)
    if (activeCustomLegendLayers.length > 0) return renderCustomLegendLayers(activeCustomLegendLayers)

    return
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        position: "absolute",
        // bottom: 32,
        top: 8,
        left: props.tmp === false && legendVis ? 30 : 3,
        zIndex: 1,
        maxHeight: "calc(100% - 130px)",
        margin: "-5px -5px -5px 0"
      }}
    >
      <div className="leaflet-left" style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: 5 }}>
        {legendVis
          ? <Paper
            elevation={2}
            className="transition"
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 40,
              minHeight: 40,
              padding: theme.spacing(0.5),
              boxSizing: "border-box",
              height: "fit-content",
              maxHeight: "100%"
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 500, fontSize: theme.typography.pxToRem(18), lineHeight: 1, margin: theme.spacing(0.5, 0.5, 0) }}>{t("Legend")}</Typography>
                <Tooltip
                  key={legendVis}
                  title={t("Hide_legend")}
                  disableInteractive
                >
                  <CloseRounded
                    onClick={() => setLegendVis(false)}
                    sx={{ cursor: "pointer" }}
                    fontSize="small"
                  />
                </Tooltip>
              </div>
              <div style={{
                display: "flex", flexDirection: "column",
                overflowX: "hidden", overflowY: "auto", maxWidth: 450, gap: theme.spacing(0.5), padding: theme.spacing(0.5)
              }}>
                {(activeGeojonLayers.length === 0 && activeWMSLayers.length === 0 && activeTDLayers.length === 0 && activeCustomLegendLayers.length === 0 && !props.children) && <Typography>{t("No_active_layers")}</Typography>}
                {activeGeojonLayers.length > 0 && renderGeoJSONLayersLegend(activeGeojonLayers)}
                {activeCustomLegendLayers.length > 0 && renderCustomLegendLayers(activeCustomLegendLayers)}
                {activeTDLayers.length > 0 && renderTDLegend(activeTDLayers)}
                {activeWMSLayers.length > 0 && renderWMSLegend(activeWMSLayers)}
                {props.children}
              </div>
            </div>
          </Paper>
          : <Tooltip
            key={legendVis}
            title={t("Show_legend")}
            disableInteractive
          >
            <Paper
              elevation={2}
              className="transition"
              onClick={() => setLegendVis(true)}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: 40,
                minHeight: 40,
                padding: theme.spacing(1),
                boxSizing: "border-box",
                cursor: "pointer",
                "&:hover": {
                  background: "#fafafa"
                }
              }}
            >
              <LegendToggleRounded color="primary" />
            </Paper>
          </Tooltip>
        }
      </div>
    </div>
  )
}

export default React.memo(Legend)